<template>
    <div class="min-h-screen bg-gradient-to-br from-[#e3f0fa] to-[#c8e0f4] text-gray-800 font-sans">
        <header class="bg-gradient-to-r from-[#1583d4] to-[#0f5fa0] text-white py-8 px-6 text-center shadow-lg">
            <h1 class="text-4xl font-extrabold mb-2">Edite seu Anúncio</h1>
            <p class="opacity-90 text-lg">Atualize as informações do seu anúncio abaixo</p>
        </header>

        <div class="container mx-auto max-w-6xl py-12 px-6">
            <div class="mb-6 flex justify-start">
                <button @click="goToHome"
                    class="bg-[#f5b400] hover:bg-[#e0a200] text-white font-semibold px-6 py-2.5 rounded-xl shadow-md transition">
                    Voltar para o Site
                </button>
            </div>
            <form @submit.prevent="handleSubmit" class="space-y-10">
                <!-- Etapa 0: Detalhes -->
                <section v-if="step === 0" class="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg p-8">
                    <h2 class="text-2xl font-semibold text-[#1583d4] mb-6">Detalhes do Anúncio</h2>
                    <div class="grid grid-cols-1 md:grid-cols-1 gap-12">
                        <v-text-field v-model="form.title" label="Título *" placeholder="Ex: Promoção de Consultoria"
                            variant="outlined" :error-messages="validationErrors.title" />
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <v-select v-model="form.category_id" label="Categoria *" variant="outlined" disabled="true"
                            :error-messages="validationErrors.category_id" :items="categories" item-title="description"
                            item-value="id" placeholder="Selecione..." />
                        <!-- Subcategoria (só aparece se a categoria tiver subcategorias) -->
                        <v-select v-if="selectedCategory && selectedCategory.subcategories.length"
                            v-model="form.subcategory" class="animate-fade-in" label="Subcategoria *" variant="outlined"
                            :error-messages="validationErrors.subcategory" :items="selectedCategory.subcategories"
                            placeholder="Selecione uma subcategoria..." />

                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div class="d-flex align-end">
                            <div class="flex-grow-1 mr-2">
                                <v-select v-model="form.coverage" label="Alcance de Atuação *" variant="outlined"
                                    :items="[
                                        { title: 'Local', value: 'local' },
                                        { title: 'Estadual', value: 'state' },
                                        { title: 'Nacional', value: 'national' },
                                    ]" item-title="title" item-value="value"
                                    :error-messages="validationErrors.coverage" placeholder="Selecione o alcance..."
                                    hide-details="auto" />
                            </div>

                            <v-menu location="top" width="320">
                                <template #activator="{ props }">
                                    <v-icon v-bind="props" size="24" color="primary" class="mb-2 ml-2">
                                        mdi-help-circle
                                    </v-icon>
                                </template>

                                <v-card class="pa-3">
                                    <h4 class="text-subtitle-1 font-weight-bold">O que é o Alcance de Atuação?</h4>
                                    <p class="text-body-2">
                                        O alcance define a **área geográfica onde sua empresa atende o cliente**.
                                        Isso ajuda o usuário a saber se você pode prestar o serviço na região dele:
                                    </p>

                                    <ul class="text-body-2 pl-4">
                                        <li>
                                            <strong>Local:</strong> Você atende ou entrega produtos/serviços apenas na
                                            sua cidade.
                                        </li>
                                        <li>
                                            <strong>Estadual:</strong> Você atende em qualquer cidade dentro do seu
                                            estado.
                                        </li>
                                        <li>
                                            <strong>Nacional:</strong> Você atende ou entrega em qualquer lugar do
                                            Brasil.
                                        </li>
                                    </ul>
                                </v-card>
                            </v-menu>
                        </div>

                        <v-textarea v-model="form.description" class="md:col-span-2" label="Descrição *"
                            placeholder="Descreva o que está anunciando..." variant="outlined"
                            :error-messages="validationErrors.description" rows="4" />
                    </div>
                </section>

                <!-- Etapa 1: Localização -->
                <section v-if="step === 1" class="bg-white rounded-2xl shadow-md p-8">
                    <h2 class="text-2xl font-semibold text-[#1583d4] mb-6">Localização</h2>
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <v-text-field v-model="form.zip_code" @blur="consultaCEP" label="CEP *" v-maska="'#####-###'"
                            :loading="isCepLoading" placeholder="00000-000" variant="outlined"
                            :error-messages="validationErrors.zip_code" />
                        <v-text-field v-model="form.city" label="Cidade *" placeholder="Cidade" variant="outlined"
                            :error-messages="validationErrors.city" />
                        <v-text-field v-model="form.state" maxlength="2" label="Estado *" placeholder="UF"
                            variant="outlined" :error-messages="validationErrors.state" />
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                        <v-text-field v-model="form.address" label="Endereço" placeholder="Rua, número..."
                            variant="outlined" />
                        <v-text-field v-model="form.neighborhood" label="Bairro" placeholder="Ex: Centro"
                            variant="outlined" />
                    </div>
                </section>

                <!-- Etapa 2: Imagem -->
                <section v-if="step === 2" class="bg-white rounded-2xl shadow-md p-8">
                    <h2 class="text-2xl font-semibold text-[#1583d4] mb-6">Imagem</h2>
                    <label for="mainImage" class="upload flex flex-col items-center justify-center text-center py-12">
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-10 h-10 text-[#1583d4]" fill="none"
                            viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                        </svg>
                        <p class="mt-3 text-[#1583d4] font-semibold">Clique para alterar a imagem principal</p>
                        <p class="text-sm text-gray-500 mt-1">Formatos aceitos: JPG, PNG, WEBP</p>
                    </label>
                    <input id="mainImage" type="file" accept="image/*" class="hidden" @change="handleImageUpload" />

                    <div v-if="form.mainImagePreview" class="mt-6 flex justify-center">
                        <img :src="form.mainImagePreview" alt="Prévia da imagem"
                            class="w-full max-w-md rounded-lg shadow-md border border-gray-300" />
                    </div>
                    <p v-if="validationErrors.mainImage" class="error">{{ validationErrors.mainImage }}</p>
                </section>

                <!-- Etapa 3: Termos -->
                <section v-if="step === 3" class="bg-white rounded-2xl shadow-md p-8">
                    <h2 class="text-2xl font-semibold text-[#1583d4] mb-6">Confirmação</h2>

                    <label class="flex items-start cursor-pointer bg-blue-50 p-4 rounded-lg">
                        <input type="checkbox" v-model="form.terms_accepted" class="mt-1 mr-3 h-5 w-5 text-[#1583d4]" />
                        <span class="text-sm text-gray-700">
                            Declaro que as informações são verdadeiras e aceito os termos de divulgação.
                        </span>
                    </label>

                    <p v-if="validationErrors.terms_accepted" class="error mt-2">{{ validationErrors.terms_accepted }}
                    </p>
                </section>

                <!-- Navegação -->
                <div class="flex justify-between pt-8">
                    <button type="button" @click="prevStep" v-if="step > 0"
                        class="px-8 py-3 rounded-xl border bg-white border-gray-300 text-gray-600 hover:bg-gray-100">
                        Voltar
                    </button>

                    <button v-if="step < steps.length - 1" type="button" @click="nextStep"
                        class="px-8 py-3 rounded-xl bg-[#1583d4] text-white hover:bg-[#0f5fa0]">
                        Avançar
                    </button>

                    <button v-else type="button" @click="showConfirmDialog = true" :disabled="isSubmitting"
                        class="px-10 py-3 bg-[#1583d4] hover:bg-[#0f5fa0] text-white rounded-xl font-semibold shadow-md disabled:opacity-50">
                        {{ isSubmitting ? 'Salvando...' : 'Salvar Alterações' }}
                    </button>
                </div>
            </form>
        </div>

        <!-- Diálogo de Confirmação -->
        <v-dialog v-model="showConfirmDialog" persistent max-width="500">
            <v-card class="rounded-2xl pa-6 text-center">
                <h2 class="text-xl font-semibold mb-4 text-gray-800">Confirmar Alterações</h2>
                <p class="text-gray-600 mb-6 leading-relaxed">
                    Ao salvar as alterações, seu anúncio será reenviado para análise e ficará com status
                    <span class="font-semibold text-yellow-600">Pendente de aprovação</span>.
                    Deseja continuar?
                </p>
                <div class="flex justify-center gap-4">
                    <v-btn color="error" @click="showConfirmDialog = false">Cancelar</v-btn>
                    <v-btn color="primary" @click="confirmSave">Confirmar</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex"
import api from "@/services/api";
import { useToastStore } from "@/store/toast";
import { useLoadingStore } from '@/store/loadingStore'

const store = useStore();
const toastStore = useToastStore();
const route = useRoute();
const router = useRouter();
const loadingStore = useLoadingStore()

const step = ref(0);
const steps = ["Detalhes", "Localização", "Imagem", "Confirmação"];
const validationErrors = ref({});
const isSubmitting = ref(false);
const categories = ref([]);
const userData = ref(null);
const isCepLoading = ref(false);
const showConfirmDialog = ref(false);
const app_url_api_storage = process.env.VUE_APP_URL_API_STORAGE;

const form = ref({
    title: "",
    category_id: "",
    subcategory: "",
    description: "",
    zip_code: "",
    city: "",
    state: "",
    address: "",
    neighborhood: "",
    mainImage: null,
    mainImagePreview: null,
    terms_accepted: false,
    coverage: "local",
});

const selectedCategory = computed(() => {
    return categories.value.find(c => c.id === form.value.category_id) || null
})

function goToHome() {
    window.location.href = '/';
}

function nextStep() {
    if (validateStep()) step.value++;
}
function prevStep() {
    step.value--;
}

function validateStep() {
    const e = {};
    if (step.value === 0) {
        if (!form.value.title.trim()) e.title = "Título obrigatório.";
        if (!form.value.category_id) e.category_id = "Categoria obrigatória.";
        if (!form.value.subcategory) e.subcategory = 'Subcategoria obrigatória.'
        if (!form.value.description.trim()) e.description = "Descrição obrigatória."
        if (!form.value.coverage) e.coverage = 'Selecione o alcance do anúncio.'
    } else if (step.value === 1) {
        if (!form.value.zip_code.trim()) e.zip_code = "CEP obrigatório.";
        if (!form.value.city.trim()) e.city = "Cidade obrigatória.";
        if (!form.value.state.trim()) e.state = "Estado obrigatório.";
    } else if (step.value === 2) {
        if (!form.value.mainImage && !form.value.mainImagePreview) e.mainImage = "Imagem principal obrigatória.";
    } else if (step.value === 3) {
        if (!form.value.terms_accepted) e.terms_accepted = "Você precisa aceitar os termos.";
    }
    validationErrors.value = e;
    return Object.keys(e).length === 0;
}

function handleImageUpload(e) {
    const file = e.target.files[0];
    if (file) {
        form.value.mainImage = file;
        form.value.mainImagePreview = URL.createObjectURL(file);
    }
}

async function consultaCEP() {
    const cep = form.value.zip_code?.replace(/\D/g, "");
    if (cep?.length === 8) {
        try {
            isCepLoading.value = true;
            const { data } = await api.get(`/cep`, { params: { cep } });
            Object.assign(form.value, {
                city: data.localidade,
                state: data.uf,
                address: data.logradouro,
                neighborhood: data.bairro,
            });
        } catch {
            toastStore.showToast({ message: "Erro ao buscar CEP", color: "error" });
        } finally {
            isCepLoading.value = false;
        }
    }
}

async function getCategories() {
    try {
        const { data } = await api.get("/categories", { params: { is_active: 1 } });
        categories.value = data.data.map(cat => ({
            ...cat,
            subcategories: cat.subcategories || []
        }))
    } catch (error) {
        toastStore.showToast({ message: error?.response?.data?.error || "Erro ao carregar categorias.", color: "error" });
    }
}

async function loadAdvertisement() {
    try {
        const { id } = route.params;
        const { data } = await api.get(`/advertisements/${id}`);
        const adv = data.data;


        let imageUrl = null;
        if (adv.images) {
            try {
                const imageArray = JSON.parse(adv.images);
                if (imageArray.length > 0) {
                    imageUrl = app_url_api_storage + '/images/' + imageArray[0];
                }
            } catch (e) {
                console.error("Erro ao parsear campo 'images':", e);
            }
        }
        Object.assign(form.value, {
            title: adv.title,
            category_id: adv.category_id,
            subcategory: adv.subcategory,
            description: adv.description,
            zip_code: adv.zip_code,
            city: adv.city,
            state: adv.state,
            address: adv.address,
            neighborhood: adv.neighborhood,
            coverage: adv.coverage,
            mainImagePreview: imageUrl || adv.main_image_url,
        });
    } catch (error) {
        toastStore.showToast({ message: error?.response?.data?.error || "Erro ao carregar anúncio.", color: "error" });
        router.push("/cadastre-seu-anuncio");
    }
}

async function confirmSave() {
    showConfirmDialog.value = false;
    await handleSubmit(true);
}

async function handleSubmit(force = false) {
    if (!validateStep()) return;
    if (!force) return (showConfirmDialog.value = true);

    isSubmitting.value = true;
    try {
        const { id } = route.params;
        const formData = new FormData();
        Object.entries(form.value).forEach(([key, value]) => {
            if (["mainImage", "mainImagePreview", "is_featured"].includes(key)) return;
            formData.append(key, value ?? "");
        });

        if (form.value.mainImage instanceof File) {
            formData.append("mainImage", form.value.mainImage);
        }
        formData.append("status", 'in_analysis');

        formData.append('_method', 'PUT');
        const response = await api.post(`/advertisements/${id}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });

        toastStore.showToast({
            message: "Anúncio atualizado e enviado para análise!",
            color: "success",
        });

        //await store.dispatch('saveAdvertisementToLocalStorage', response.data.data);

        router.push("/cadastre-seu-anuncio");
    } catch (error) {
        toastStore.showToast({
            message: error?.response?.data?.error || "Erro ao atualizar anúncio.",
            color: "error",
        });
    } finally {
        isSubmitting.value = false;
    }
}

onMounted(async () => {
    const stored = localStorage.getItem("userData");
    if (stored) userData.value = JSON.parse(stored).user;
    loadingStore.show()
    await getCategories();
    await loadAdvertisement();
    loadingStore.hide()
});
</script>

<style scoped>
.upload {
    @apply cursor-pointer border-2 border-dashed border-[#1583d4] rounded-xl w-full py-10 text-center hover:bg-blue-50 transition;
}

.error {
    @apply mt-1 text-sm text-red-500;
}
</style>
